import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { CopyIconButton } from '~/components/common/copy-icon-button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~/components/ui/sheet';
import { ProductPager } from '~/features/catalog-operations/components/product-pager';

import type { TimelineIdType } from '../../constants/enums';
import { useProductTimeline } from '../../hooks/use-product-timeline';
import { isGmcConnectorCode } from '../../utils/workspace-connector';

import { ProductDeliveryHistory } from './product-delivery-history';

/** Deliveries shown per product — a product's history is a handful of jobs. */
const LOG_LIMIT = 25;

/** Prev / next between the host list's products, rendered at the top of the sheet. */
export type ProductLogPager = Omit<ComponentProps<typeof ProductPager>, 'className'>;

/** What the caller already knows about the product (for the header). */
export interface ProductLogSubject {
  /** The id the timeline is looked up by — the retailer SKU, GMC's offer id, or
   *  (with `idType: 'source'`) the source system's id. */
  productId: string;
  /** SKU to display / deep-link with when `productId` isn't the SKU (source-id lookups). */
  sku?: string | null;
  title?: string | null;
  imageUrl?: string | null;
  externalId?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  /** Connector of the run / connection — picks the id type and the destination's wording. */
  connectorCode: string | null | undefined;
  subject: ProductLogSubject | null;
  /** Delivery job to open first (the run the user came from); else the newest. */
  focusJobId?: string | null | undefined;
  /** Override the id type (default: `external` for GMC, `retailer` otherwise). */
  idType?: TimelineIdType;
  /** Step through the host list without closing the sheet; omitted → no arrows. */
  pager?: ProductLogPager | undefined;
  /** `data-testid` for the sheet content; header, status, refresh and states derive from it. */
  testId?: string;
}

/** GMC rows are matched by offer id (`external`); every other connector by retailer SKU. */
function idTypeFor(connectorCode: string | null | undefined): TimelineIdType {
  return isGmcConnectorCode(connectorCode) ? 'external' : 'retailer';
}

/**
 * Product delivery log — one product's delivery history on a connection, in a
 * side sheet that keeps the caller's context (a run's products, a blocked
 * worklist, the Compare page). Header: identity plus the latest ingest and
 * approval state; then every delivery job newest first, each with its
 * lifecycle strip, reason, fields and a diff against the delivery before it.
 * Payloads are requested so the diff works. Built on the per-product timeline
 * (`GET deliveries/{cid}/products/{pid}/timeline`); true step-by-step history
 * needs the backend event log (see docs/product-delivery-log.md).
 */
export function ProductDeliveryLogSheet({
  open,
  onOpenChange,
  connectionId,
  connectorCode,
  subject,
  focusJobId,
  idType,
  pager,
  testId = 'product-delivery-log-sheet',
}: Props) {
  const { t } = useTranslation('deliveries');
  const productId = subject?.productId ?? '';
  const query = useProductTimeline(
    connectionId,
    productId,
    { idType: idType ?? idTypeFor(connectorCode), limit: LOG_LIMIT, includePayload: true },
    open && productId !== '',
  );
  const entries = query.data?.entries ?? [];
  const latest = entries[0];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Full width below 1280px; 60% of the viewport from xl up, so diffs and payloads have room. */}
      <SheetContent
        side="right"
        data-testid={testId}
        className="w-full overflow-y-auto sm:max-w-none xl:w-[60vw] xl:max-w-[60vw]"
      >
        {subject ? (
          <>
            <SheetHeader>
              {/* `pr-8` keeps the pager clear of the sheet's close button. */}
              <div className="flex items-start gap-3 pr-8">
                {subject.imageUrl ? (
                  <CachedImage
                    src={subject.imageUrl}
                    alt={subject.title ?? subject.sku ?? subject.productId}
                    className="size-12 shrink-0 rounded-lg border"
                  />
                ) : null}
                <div className="min-w-0 flex-1 space-y-1">
                  <SheetTitle data-testid={`${testId}-title`} className="text-base leading-snug">
                    {subject.title ?? subject.sku ?? subject.productId}
                  </SheetTitle>
                  <SheetDescription asChild>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      <span className="inline-flex items-center gap-1">
                        <span className="text-muted-foreground">{t('productLog.sheet.sku')}</span>
                        <span
                          data-testid={`${testId}-sku`}
                          className="text-foreground font-mono break-all"
                        >
                          {subject.productId}
                        </span>
                        <CopyIconButton
                          text={subject.productId}
                          label={t('productLog.sheet.copySku')}
                        />
                      </span>
                      {latest?.externalId &&
                      latest.externalId !== (subject.sku ?? subject.productId) ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-muted-foreground">
                            {t('productLog.sheet.externalId')}
                          </span>
                          <span
                            data-testid={`${testId}-external-id`}
                            className="text-foreground font-mono break-all"
                          >
                            {latest.externalId}
                          </span>
                          <CopyIconButton
                            text={latest.externalId}
                            label={t('productLog.sheet.copyExternalId')}
                          />
                        </span>
                      ) : null}
                    </div>
                  </SheetDescription>
                </div>
                {pager ? <ProductPager {...pager} className="ml-auto" /> : null}
              </div>
            </SheetHeader>

            <ProductDeliveryHistory
              connectionId={connectionId}
              connectorCode={connectorCode}
              subject={subject}
              focusJobId={focusJobId}
              idType={idType}
              enabled={open}
              testId={testId}
            />
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
